// Browser fetch client for the Backoffice BFF API — used by the frontend and Kubb-generated clients.
// Shape matches Kubb's ResponseConfig so `return res.data` in generated code is type-safe.
//
// @see https://kubb.dev/kubb/guide/fetch
let BASE_URL =
  (typeof import.meta === 'undefined'
    ? undefined
    : (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env
        ?.VITE_API_URL) ?? 'http://localhost:3004';

/** Optional — set from Keycloak (or any OIDC) so every BFF request sends Bearer. */
let resolveAccessToken: (() => Promise<string | undefined>) | null = null;

export function setAccessTokenProvider(fn: (() => Promise<string | undefined>) | null) {
  resolveAccessToken = fn;
}

let handleUnauthorized: (() => void) | null = null;
let unauthorizedInFlight = false;

export function setUnauthorizedHandler(fn: (() => void) | null) {
  handleUnauthorized = fn;
  if (!fn) {
    unauthorizedInFlight = false;
  }
}

function notifyUnauthorized(): void {
  if (unauthorizedInFlight || !handleUnauthorized) return;
  unauthorizedInFlight = true;
  try {
    handleUnauthorized();
  } catch (err) {
    unauthorizedInFlight = false;
    console.error('[backoffice-api-sdk] unauthorized handler threw', err);
  }
}

export function setBaseUrl(url: string) {
  BASE_URL = url.replace(/\/+$/, '');
}

export interface RequestConfig<TBody = unknown> {
  method: string;
  url: string;
  params?: Record<string, unknown>;
  data?: TBody;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

/** Used in generated client generics for OpenAPI error bodies (runtime errors are thrown). */
export type ResponseErrorConfig<T> = {
  error: T;
  status: number;
};

/** Kubb-compatible success envelope — same as @kubb/plugin-client fetch guide. */
export type ResponseConfig<TData = unknown> = {
  data: TData;
  status: number;
  statusText: string;
};

export type Client = <TData, _TError = unknown, TBody = unknown>(
  config: RequestConfig<TBody>
) => Promise<ResponseConfig<TData>>;

function buildUrl(path: string, params?: Record<string, unknown>): string {
  const url = new URL(path, BASE_URL);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      if (Array.isArray(v)) {
        for (const item of v) {
          if (item === undefined || item === null) continue;
          url.searchParams.append(k, String(item));
        }
        return;
      }
      url.searchParams.append(k, String(v));
    });
  }
  return url.toString();
}

function resolveFetchSignal(userSignal: AbortSignal | undefined): AbortSignal | undefined {
  const meta =
    typeof import.meta !== 'undefined' && import.meta.env && typeof import.meta.env === 'object'
      ? (import.meta.env as Record<string, string | undefined>)
      : {};
  const raw = meta.VITE_API_REQUEST_TIMEOUT_MS;
  const timeoutMs = raw != null && String(raw) !== '' ? Number(raw) : 30_000;
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    return userSignal;
  }
  const TimeoutSignal =
    typeof AbortSignal !== 'undefined'
      ? (AbortSignal as typeof AbortSignal & { timeout?: (ms: number) => AbortSignal }).timeout
      : undefined;
  if (typeof TimeoutSignal !== 'function') {
    return userSignal;
  }
  const timeoutSignal = TimeoutSignal(timeoutMs);
  if (!userSignal) {
    return timeoutSignal;
  }
  const anyFn = (
    AbortSignal as typeof AbortSignal & { any?: (signals: AbortSignal[]) => AbortSignal }
  ).any;
  if (typeof anyFn === 'function') {
    return anyFn([userSignal, timeoutSignal]);
  }
  return userSignal;
}

export async function fetchClient<TData, _TError = unknown, TBody = unknown>(
  config: RequestConfig<TBody>
): Promise<ResponseConfig<TData>> {
  const { method, url, params, data, headers: customHeaders, signal } = config;
  const isBodyMethod = !['GET', 'HEAD'].includes(method.toUpperCase());

  // Only set the JSON content-type when we actually have a body — Fastify
  // rejects empty `application/json` payloads with FST_ERR_CTP_EMPTY_JSON_BODY.
  const hasJsonBody = isBodyMethod && data != null;
  const headers: Record<string, string> = {
    ...(hasJsonBody ? { 'content-type': 'application/json' } : {}),
    ...(customHeaders ?? {}),
  };

  const token = resolveAccessToken ? await resolveAccessToken() : undefined;
  if (token) {
    headers.authorization = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(url, params), {
      method,
      headers,
      ...(isBodyMethod && data != null ? { body: JSON.stringify(data) } : {}),
      signal: resolveFetchSignal(signal),
    });
  } catch (err) {
    const wrapped = new Error((err as Error).message ?? 'Network error') as Error & {
      status: number;
      body: unknown;
    };
    wrapped.status = 0;
    wrapped.body = { message: (err as Error).message };
    throw wrapped;
  }

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    if (res.status === 401) {
      notifyUnauthorized();
    }
    const err = new Error('Request failed') as Error & { status: number; body: unknown };
    err.status = res.status;
    err.body = payload;
    throw err;
  }

  return {
    data: payload as TData,
    status: res.status,
    statusText: res.statusText,
  };
}

export default fetchClient;
